import { Routes } from '@angular/router';

import { authGuard } from './features/authentication/guards/auth.guard';
import { establishmentGuard } from './features/establishment/guards/establishment.guard';
import { adminRoleGuard } from './features/establishment/guards/role.guard';
import { onboardingGuard } from './features/onboarding/guards/onboarding.guard';
import { reverseOnboardingGuard } from './features/onboarding/guards/reverse-onboarding.guard';
import { AppShellComponent } from './layout/app-shell/app-shell.component';

export const routes: Routes = [
  // Auth routes (no shell)
  {
    path: 'auth',
    loadChildren: () => import('./features/authentication/auth.routes').then((m) => m.AUTH_ROUTES),
  },
  // Pre-shell onboarding routes (no shell, no establishment needed)
  // Handles: /onboarding/setup, /onboarding/create-establishment, /onboarding/pending-invitations
  {
    path: 'onboarding',
    canActivate: [authGuard],
    loadChildren: () =>
      import('./features/onboarding/onboarding.routes').then((m) => m.ONBOARDING_ROUTES),
  },
  // Evidence upload portal (public — token-based access, no login required)
  {
    path: 'evidence-upload/:token',
    loadComponent: () =>
      import('./pages/evidence-upload/evidence-upload.component').then(
        (m) => m.EvidenceUploadComponent,
      ),
  },
  {
    path: 'evidence-upload/:token/confirmation',
    loadComponent: () =>
      import('./pages/evidence-upload/evidence-upload-confirmation.component').then(
        (m) => m.EvidenceUploadConfirmationComponent,
      ),
  },
  // Protected routes (with shell) - requires both auth and establishment
  // AppShell routes: onboarding wizard (no onboardingGuard — these ARE the onboarding)
  // Handles: /onboarding/complete (wizard), /onboarding/contract (Phase 38)
  {
    path: 'onboarding',
    component: AppShellComponent,
    canActivate: [authGuard, establishmentGuard, reverseOnboardingGuard],
    children: [
      {
        path: 'complete',
        loadComponent: () =>
          import('./features/onboarding/complete/complete.component').then(
            (m) => m.OnboardingCompleteComponent,
          ),
      },
      {
        path: 'contract',
        loadComponent: () =>
          import('./features/onboarding/contract/contract.component').then(
            (m) => m.OnboardingContractComponent,
          ),
      },
    ],
  },
  // AppShell routes: profile (accessible during onboarding — no onboardingGuard)
  {
    path: '',
    component: AppShellComponent,
    canActivate: [authGuard, establishmentGuard],
    children: [
      {
        path: 'profile',
        loadComponent: () =>
          import('./pages/profile/profile.component').then((m) => m.ProfileComponent),
      },
    ],
  },
  // AppShell routes: main app (with onboardingGuard — blocks incomplete establishments)
  {
    path: '',
    component: AppShellComponent,
    canActivate: [authGuard, establishmentGuard, onboardingGuard],
    children: [
      {
        path: 'dashboard',
        loadComponent: () =>
          import('./features/dashboard/dashboard.component').then((m) => m.DashboardComponent),
      },
      {
        path: 'create-link',
        loadComponent: () =>
          import('./pages/create-link/create-link.component').then((m) => m.CreateLinkComponent),
      },
      {
        path: 'my-links',
        loadComponent: () =>
          import('./pages/my-links/my-links.component').then((m) => m.MyLinksComponent),
      },
      {
        path: 'transactions',
        canActivate: [adminRoleGuard],
        loadComponent: () =>
          import('./pages/transactions/transactions.component').then(
            (m) => m.TransactionsComponent,
          ),
      },
      {
        path: 'pos',
        canActivate: [adminRoleGuard],
        loadComponent: () => import('./pages/pos/pos.component').then((m) => m.PosComponent),
      },
      {
        path: 'end-report',
        canActivate: [adminRoleGuard],
        loadComponent: () =>
          import('./pages/end-report/end-report.component').then((m) => m.EndReportComponent),
      },
      {
        path: '',
        redirectTo: 'dashboard',
        pathMatch: 'full',
      },
    ],
  },
  {
    path: '**',
    redirectTo: 'dashboard',
  },
];
